import { useState, useEffect, useCallback } from 'react';
import { fetchMappingGrouped, groupMappingAction, bulkGroupMappingAction, fetchMappingCandidates, approveMapping } from '@/api';
import { LoadingPage } from '@/components/common/Loading';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GitMerge, Search, ChevronLeft, ChevronRight, EyeOff, Ban, Check, X, Layers, ArrowUpDown, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pending:       { label: 'Pending',       color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  approved:      { label: 'Approved',      color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  auto_approved: { label: 'Auto Approved', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  rejected:      { label: 'Rejected',      color: 'bg-red-500/10 text-red-400 border-red-500/20' },
  ignored:       { label: 'Ignored',       color: 'bg-slate-500/10 text-slate-400 border-slate-500/20' },
  unsupported:   { label: 'Unsupported',   color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
};

const FREQ_CONFIG = {
  high:   { label: 'High Freq',   color: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
  medium: { label: 'Medium',      color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  low:    { label: 'Low Freq',    color: 'bg-slate-500/10 text-slate-400 border-slate-500/20' },
};

export default function MappingQueue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');
  const [sortBy, setSortBy] = useState('frequency');
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [processing, setProcessing] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const load = useCallback(() => {
    setLoading(true);
    fetchMappingGrouped({
      status,
      sort_by: sortBy,
      page,
      limit: 20,
      search: searchDebounced || undefined,
    })
      .then(res => { setData(res.data); setLoading(false); setSelected(new Set()); })
      .catch(() => setLoading(false));
  }, [status, sortBy, page, searchDebounced]);

  useEffect(() => { load(); }, [load]);

  const handleGroupAction = async (sourceValue, action) => {
    setProcessing(true);
    try {
      const res = await groupMappingAction({ source_value: sourceValue, action });
      const labels = { ignored: 'Ignored', unsupported: 'Unsupported', rejected: 'Rejected' };
      toast.success(`${labels[res.data.action] || action}: ${res.data.affected} items updated`);
      load();
    } catch { toast.error('Action failed'); }
    setProcessing(false);
  };

  const handleBulkGroupAction = async (action) => {
    if (selected.size === 0) return;
    setProcessing(true);
    try {
      const res = await bulkGroupMappingAction({ source_values: [...selected], action });
      const labels = { ignored: 'Ignored', unsupported: 'Unsupported', rejected: 'Rejected' };
      toast.success(`${labels[res.data.action] || action}: ${res.data.affected} items across ${res.data.groups_processed} groups`);
      load();
    } catch { toast.error('Bulk action failed'); }
    setProcessing(false);
  };

  const toggleSelect = (sv) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(sv)) next.delete(sv); else next.add(sv);
      return next;
    });
  };

  const toggleAll = () => {
    if (!data?.groups) return;
    if (selected.size === data.groups.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.groups.map(g => g.source_value)));
    }
  };

  const groups = data?.groups || [];

  return (
    <div data-testid="mapping-queue-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
            <GitMerge className="w-5 h-5 inline mr-2 text-blue-400" />
            Mapping Queue
          </h1>
          {data?.counts && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                data.counts[key] > 0 && (
                  <Badge key={key} className={`text-xs cursor-pointer ${cfg.color} ${status === key ? 'ring-1 ring-white/30' : ''}`}
                    onClick={() => { setStatus(key); setPage(1); }}>
                    {cfg.label}: {data.counts[key]}
                  </Badge>
                )
              ))}
              <Badge className="bg-slate-700/50 text-slate-300 border-slate-600 text-xs">
                Total: {data.counts.total}
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search market types..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
            className="pl-9 bg-slate-900 border-slate-700 text-sm h-9"
            data-testid="mapping-search"
          />
        </div>
        <Select value={status} onValueChange={v => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700 text-sm h-9" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={v => { setSortBy(v); setPage(1); }}>
          <SelectTrigger className="w-[150px] bg-slate-900 border-slate-700 text-sm h-9" data-testid="sort-filter">
            <ArrowUpDown className="w-3.5 h-3.5 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="frequency">By Frequency</SelectItem>
            <SelectItem value="confidence">By Confidence</SelectItem>
            <SelectItem value="alphabetical">Alphabetical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions */}
      {status === 'pending' && selected.size > 0 && (
        <div className="flex items-center gap-2 mb-3 p-3 bg-slate-900/80 border border-slate-700 rounded-lg" data-testid="bulk-actions-bar">
          <span className="text-xs text-slate-400 font-medium">{selected.size} group{selected.size > 1 ? 's' : ''} selected</span>
          <div className="flex gap-1.5 ml-auto">
            <Button size="sm" onClick={() => handleBulkGroupAction('ignore')} disabled={processing}
              className="bg-slate-500/10 text-slate-400 hover:bg-slate-500/20 border border-slate-500/20 h-7 text-xs"
              data-testid="bulk-ignore">
              <EyeOff className="w-3 h-3 mr-1" /> Ignore All
            </Button>
            <Button size="sm" onClick={() => handleBulkGroupAction('unsupported')} disabled={processing}
              className="bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 border border-orange-500/20 h-7 text-xs"
              data-testid="bulk-unsupported">
              <Ban className="w-3 h-3 mr-1" /> Unsupported All
            </Button>
            <Button size="sm" onClick={() => handleBulkGroupAction('reject')} disabled={processing}
              className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 h-7 text-xs"
              data-testid="bulk-reject">
              <X className="w-3 h-3 mr-1" /> Reject All
            </Button>
          </div>
        </div>
      )}

      {loading ? <LoadingPage /> : (
        <>
          {/* Select All */}
          {status === 'pending' && groups.length > 0 && (
            <div className="flex items-center gap-2 mb-3 px-1">
              <input type="checkbox" checked={selected.size === groups.length && groups.length > 0}
                onChange={toggleAll}
                className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 accent-emerald-500"
                data-testid="select-all-checkbox" />
              <span className="text-xs text-slate-500">Select all groups</span>
              <span className="text-xs text-slate-600 ml-auto">{data?.total_groups || 0} unique markets</span>
            </div>
          )}

          {/* Grouped Items */}
          <div className="space-y-2" data-testid="mapping-groups">
            {groups.length > 0 ? groups.map(group => (
              <GroupedMappingItem
                key={group.source_value}
                group={group}
                isPending={status === 'pending'}
                isSelected={selected.has(group.source_value)}
                onToggle={() => toggleSelect(group.source_value)}
                onAction={(action) => handleGroupAction(group.source_value, action)}
                processing={processing}
                onRefresh={load}
              />
            )) : (
              <div className="text-center py-12 text-slate-500">
                <Layers className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                <p className="text-sm">No items in this category</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {data?.pages > 1 && (
            <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
              <span>Page {data.page} of {data.pages} ({data.total_groups} groups)</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="h-7 border-slate-700 text-slate-400" data-testid="prev-page">
                  <ChevronLeft className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}
                  className="h-7 border-slate-700 text-slate-400" data-testid="next-page">
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GroupedMappingItem({ group, isPending, isSelected, onToggle, onAction, processing, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [canonicalId, setCanonicalId] = useState(group.suggested_canonical_id || '');
  const [filterText, setFilterText] = useState('');
  const [approving, setApproving] = useState(false);

  const freqCfg = FREQ_CONFIG[group.frequency_category] || FREQ_CONFIG.low;

  const loadCandidates = async () => {
    if (candidates.length > 0) return;
    try {
      const res = await fetchMappingCandidates(group.entity_type);
      setCandidates(res.data.candidates || []);
    } catch { /* ignore */ }
  };

  const handleApproveGroup = async () => {
    if (!canonicalId) { toast.error('Select a canonical market'); return; }
    setApproving(true);
    try {
      let count = 0;
      for (const id of group.ids) {
        await approveMapping({ mapping_id: id, canonical_id: canonicalId });
        count++;
      }
      toast.success(`Approved ${count} items for "${group.source_value}"`);
      setShowApprove(false);
      onRefresh();
    } catch { toast.error('Approve failed'); }
    setApproving(false);
  };

  const filteredCandidates = candidates.filter(c =>
    !filterText || c.name.toLowerCase().includes(filterText.toLowerCase()) || c.id.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <div className={`border rounded-lg transition-all ${isSelected ? 'border-blue-500/40 bg-blue-500/5' : 'border-slate-800 bg-slate-900/50'}`}
      data-testid={`group-${group.source_value}`}>
      {/* Group Header */}
      <div className="p-4 flex items-start gap-3">
        {isPending && (
          <input type="checkbox" checked={isSelected} onChange={onToggle}
            className="mt-1.5 w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 accent-emerald-500" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <p className="text-sm font-semibold text-slate-100">"{group.source_value}"</p>
            <Badge className={`text-xs ${freqCfg.color}`} data-testid={`freq-${group.source_value}`}>
              {freqCfg.label} ({group.count}x)
            </Badge>
            {group.max_confidence > 0 && (
              <Badge className={`text-xs ${
                group.max_confidence >= 85 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                group.max_confidence >= 60 ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                'bg-red-500/10 text-red-400 border-red-500/20'
              }`}>
                {group.max_confidence}% match
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span>{group.bookmaker_count} bookmaker{group.bookmaker_count > 1 ? 's' : ''}</span>
            {group.suggested_canonical_id && (
              <span className="text-blue-400">Suggested: {group.suggested_canonical_id}</span>
            )}
          </div>

          {/* Expanded: show bookmakers list */}
          {expanded && (
            <div className="mt-3 p-3 bg-slate-800/50 rounded border border-slate-700/50">
              <p className="text-xs text-slate-400 mb-1.5 font-medium">Bookmakers:</p>
              <div className="flex flex-wrap gap-1.5">
                {group.bookmakers.map(bkm => (
                  <Badge key={bkm} variant="outline" className="text-xs border-slate-600 text-slate-400">{bkm}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Approve panel */}
          {showApprove && (
            <div className="mt-3 space-y-2 p-3 bg-slate-800/50 rounded border border-emerald-500/20">
              <p className="text-xs text-slate-400 font-medium">Map to canonical market:</p>
              <Input
                placeholder="Filter candidates..."
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                className="bg-slate-800 border-slate-700 text-xs h-7"
              />
              <div className="max-h-32 overflow-y-auto space-y-0.5 border border-slate-700 rounded p-1 bg-slate-800/80">
                {filteredCandidates.slice(0, 20).map(c => (
                  <button key={c.id} onClick={() => { setCanonicalId(c.id); setFilterText(''); }}
                    className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-slate-700 transition-colors ${
                      canonicalId === c.id ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300'
                    }`}>
                    {c.name} <span className="text-slate-500">({c.id})</span>
                  </button>
                ))}
                {filteredCandidates.length === 0 && <p className="text-xs text-slate-500 px-2 py-1">No matches</p>}
              </div>
              <div className="flex gap-2">
                <Input value={canonicalId} onChange={e => setCanonicalId(e.target.value)}
                  placeholder="Or type canonical ID..."
                  className="bg-slate-800 border-slate-700 text-xs h-7 flex-1" />
                <Button size="sm" onClick={handleApproveGroup} disabled={approving || !canonicalId}
                  className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 h-7 text-xs">
                  <Check className="w-3 h-3 mr-1" /> Approve ({group.count})
                </Button>
                <Button size="sm" onClick={() => setShowApprove(false)}
                  className="bg-slate-700 text-slate-400 hover:bg-slate-600 h-7 text-xs">Cancel</Button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="ghost" onClick={() => setExpanded(!expanded)}
            className="text-slate-500 hover:text-slate-300 h-8 w-8 p-0" data-testid={`expand-${group.source_value}`}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          {isPending && (
            <>
              <Button size="sm" onClick={() => { setShowApprove(true); loadCandidates(); }} disabled={processing}
                className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 h-8"
                title="Approve group"
                data-testid={`approve-group-${group.source_value}`}>
                <Check className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={() => onAction('ignore')} disabled={processing}
                className="bg-slate-500/10 text-slate-400 hover:bg-slate-500/20 border border-slate-500/20 h-8"
                title="Ignore (low priority)"
                data-testid={`ignore-group-${group.source_value}`}>
                <EyeOff className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={() => onAction('unsupported')} disabled={processing}
                className="bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 border border-orange-500/20 h-8"
                title="Mark unsupported"
                data-testid={`unsupported-group-${group.source_value}`}>
                <Ban className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={() => onAction('reject')} disabled={processing}
                className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 h-8"
                title="Reject"
                data-testid={`reject-group-${group.source_value}`}>
                <X className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
